/**
 * Module Achats (/achats/:onglet) — pilotage des approvisionnements.
 * Onglets : À traiter · Documents · Commandes · Fournisseurs.
 * Rapprochement Commande → Bon de livraison → Facture avec détection des écarts (qté/prix),
 * puis validation en stock (réutilise la logique d'entrée du scanner).
 * Réservé aux rôles financiers (gérant, sommelier).
 */
import { useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FileText, Package, Truck, Building2, AlertTriangle, CheckCircle2, Upload, Loader2, Plus, ShoppingBag, Trash2, ArrowRight } from 'lucide-react';
import { achatsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button, EmptyState, Spinner, PageTitle, Tag } from '../../components/ui/kit';
import { formatEuro, formatDate } from '../../utils/format';

const TABS = [
  { key: 'a-traiter', label: 'À traiter', icon: AlertTriangle },
  { key: 'a-commander', label: 'À commander', icon: ShoppingBag },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'commandes', label: 'Commandes', icon: Package },
  { key: 'fournisseurs', label: 'Fournisseurs', icon: Building2 },
];

const TAB_COUNT_KEY = {
  'a-traiter': 'a_traiter', 'a-commander': 'a_commander', documents: 'documents',
  commandes: 'commandes', fournisseurs: 'fournisseurs',
};

const DOC_STATUS = {
  a_valider: { label: 'À valider', tone: 'fill' },
  valide: { label: 'Validé', tone: 'neutral' },
};
const ORDER_STATUS = {
  brouillon: { label: 'Brouillon', tone: 'neutral' },
  commandee: { label: 'Commandée', tone: 'fill' },
  close: { label: 'Clôturée', tone: 'neutral' },
};

export default function Achats() {
  const { onglet } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const active = TABS.find((t) => t.key === onglet) ? onglet : 'a-traiter';

  const { data: summary } = useQuery({ queryKey: ['achats-summary'], queryFn: achatsApi.summary });

  if (user && user.role === 'serveur') return <Navigate to="/" replace />;

  return (
    <div className="p-4 md:p-6 max-w-6xl">
      <PageTitle kicker="Approvisionnements" title="Achats" />
      {/* Onglets */}
      <div className="flex flex-wrap gap-1 mt-4 mb-6 border-b border-hair" data-testid="achats-tabs">
        {TABS.map((t) => {
          const count = summary?.[TAB_COUNT_KEY[t.key]];
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              data-testid={`achats-tab-${t.key}`}
              onClick={() => navigate(`/achats/${t.key}`)}
              className={`flex items-center gap-2 px-3 h-10 text-[13.5px] border-b-2 -mb-px transition-colors ${
                isActive ? 'border-gold text-ink' : 'border-transparent text-n600 hover:text-ink'
              }`}
            >
              <t.icon className="w-4 h-4" strokeWidth={1.6} />
              {t.label}
              {count > 0 && <span className="nums text-n500">· {count}</span>}
            </button>
          );
        })}
      </div>

      {active === 'a-traiter' && <ATraiter />}
      {active === 'a-commander' && <ACommander />}
      {active === 'documents' && <Documents />}
      {active === 'commandes' && <Commandes />}
      {active === 'fournisseurs' && <Fournisseurs />}
    </div>
  );
}

/* ---------------- À traiter ---------------- */
function ATraiter() {
  const { data, isLoading } = useQuery({ queryKey: ['achats-a-traiter'], queryFn: achatsApi.aTraiter });
  const [docId, setDocId] = useState(null);
  if (isLoading) return <Spinner label="Chargement…" />;
  const { documents = [], orders = [] } = data || {};
  if (!documents.length && !orders.length)
    return <EmptyState title="Rien à traiter" description="Les factures à valider et les commandes en attente apparaîtront ici." testId="a-traiter-empty" />;

  return (
    <div className="space-y-6" data-testid="achats-a-traiter">
      {documents.length > 0 && (
        <section>
          <h3 className="font-title text-2xl mb-2">Documents à valider</h3>
          <div className="space-y-2">
            {documents.map((d) => (
              <div key={d.id} className="vd-card p-3 flex items-center gap-3" data-testid={`a-traiter-doc-${d.id}`}>
                <FileText className="w-5 h-5 text-gold700 shrink-0" strokeWidth={1.6} />
                <div className="flex-1 min-w-0">
                  <p className="font-title text-lg truncate">{d.supplier_name || 'Fournisseur inconnu'} · {d.type}</p>
                  <p className="text-[12px] text-n600">N° {d.number || '—'} · {d.lines?.length || 0} ligne(s) · {formatEuro(d.total_ht)}</p>
                </div>
                <Button variant="primary" onClick={() => setDocId(d.id)} data-testid={`a-traiter-open-${d.id}`}>Traiter</Button>
              </div>
            ))}
          </div>
        </section>
      )}
      {orders.length > 0 && (
        <section>
          <h3 className="font-title text-2xl mb-2">Commandes en attente</h3>
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="vd-card p-3 flex items-center gap-3" data-testid={`a-traiter-order-${o.id}`}>
                <Package className="w-5 h-5 text-n600 shrink-0" strokeWidth={1.6} />
                <div className="flex-1 min-w-0">
                  <p className="font-title text-lg truncate">{o.supplier_name}</p>
                  <p className="text-[12px] text-n600">{o.total_bottles} bouteille(s) · {formatEuro(o.total_ht)} · {formatDate(o.date)}</p>
                </div>
                <Tag tone={ORDER_STATUS[o.status]?.tone}>{ORDER_STATUS[o.status]?.label || o.status}</Tag>
              </div>
            ))}
          </div>
        </section>
      )}
      {docId && <DocumentModal documentId={docId} onClose={() => setDocId(null)} />}
    </div>
  );
}

/* ---------------- À commander (souhaits) ---------------- */
function ACommander() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: wishes = [], isLoading } = useQuery({ queryKey: ['achats-wishes'], queryFn: achatsApi.wishes });
  const [selected, setSelected] = useState({});
  const refresh = () => { qc.invalidateQueries({ queryKey: ['achats-wishes'] }); qc.invalidateQueries({ queryKey: ['achats-summary'] }); };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = wishes.filter((w) => selected[w.id]).map((w) => w.id);

  const setQty = async (id, quantity) => { await achatsApi.updateWish(id, { quantity }); refresh(); };
  const remove = async (id) => { await achatsApi.deleteWish(id); refresh(); toast.success('Souhait retiré'); };
  const toOrder = async () => {
    const res = await achatsApi.wishesToOrder(selectedIds);
    setSelected({});
    refresh();
    qc.invalidateQueries({ queryKey: ['achats-orders'] });
    qc.invalidateQueries({ queryKey: ['achats-a-traiter'] });
    toast.success(`${res.orders_created} commande(s) créée(s) en brouillon`);
  };

  if (isLoading) return <Spinner />;
  if (!wishes.length)
    return <EmptyState title="Aucun souhait de commande" description="Ajoutez des vins depuis une fiche bouteille ou une fiche domaine (« Ajouter à la commande »)." testId="a-commander-empty" />;

  // Regroupement par fournisseur.
  const groups = {};
  wishes.forEach((w) => { (groups[w.supplier_name || 'Fournisseur'] ||= []).push(w); });

  return (
    <div className="space-y-6" data-testid="achats-a-commander">
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between vd-card p-3 sticky top-0 z-10">
          <span className="text-[13px] text-n700">{selectedIds.length} souhait(s) sélectionné(s)</span>
          <Button variant="primary" onClick={toOrder} data-testid="wishes-to-order-btn"><ArrowRight className="w-4 h-4" /> Transformer en commande</Button>
        </div>
      )}
      {Object.entries(groups).map(([supplier, items]) => (
        <section key={supplier}>
          <h3 className="font-title text-2xl mb-2">{supplier}</h3>
          <div className="space-y-2">
            {items.map((w) => (
              <div key={w.id} className="vd-card p-3 flex items-center gap-3" data-testid={`wish-${w.id}`}>
                <input type="checkbox" checked={!!selected[w.id]} onChange={() => toggle(w.id)} className="accent-[#b68235] w-4 h-4" data-testid={`wish-check-${w.id}`} />
                <div className="flex-1 min-w-0">
                  {w.cuvee_id ? (
                    <button onClick={() => navigate(`/cave/cuvee/${w.cuvee_id}`)} className="font-title text-lg truncate text-left hover:text-gold700 hover:underline" data-testid={`wish-cuvee-link-${w.id}`}>
                      {w.cuvee_name} {w.year && <span className="text-n600">{w.year}</span>}
                    </button>
                  ) : (
                    <p className="font-title text-lg truncate">{w.cuvee_name} {w.year && <span className="text-n600">{w.year}</span>}</p>
                  )}
                  <p className="text-[12px] text-n600">{w.format} · demandé le {formatDate(w.created_at)}{w.note ? ` · ${w.note}` : ''}</p>
                </div>
                <label className="flex items-center gap-1 text-[12px] text-n600">
                  Qté
                  <input type="number" min="1" value={w.quantity} onChange={(e) => setQty(w.id, Number(e.target.value))} className="vd-input h-8 w-16 nums text-[13px]" data-testid={`wish-qty-${w.id}`} />
                </label>
                <button onClick={() => remove(w.id)} className="p-1.5 text-n500 hover:text-red-700" data-testid={`wish-delete-${w.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
function Documents() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ['achats-documents'], queryFn: () => achatsApi.documents() });
  const [docId, setDocId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await achatsApi.uploadDocument(file);
      qc.invalidateQueries({ queryKey: ['achats-documents'] });
      qc.invalidateQueries({ queryKey: ['achats-summary'] });
      toast.success('Document importé — OCR effectué');
    } catch (err) {
      toast.error(err.message || "Échec de l'import");
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div data-testid="achats-documents">
      <div className="flex justify-end mb-3">
        <label className="vd-btn vd-btn-primary h-9 cursor-pointer" data-testid="achats-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.6} />}
          Importer une facture / BL
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onUpload} disabled={uploading} />
        </label>
      </div>
      {isLoading ? <Spinner /> : !data.length ? (
        <EmptyState title="Aucun document" description="Importez une facture ou un bon de livraison." testId="documents-empty" />
      ) : (
        <div className="space-y-2">
          {data.map((d) => (
            <button key={d.id} onClick={() => setDocId(d.id)} className="w-full vd-card p-3 flex items-center gap-3 text-left hover:bg-surface transition-colors" data-testid={`document-${d.id}`}>
              <FileText className="w-5 h-5 text-gold700 shrink-0" strokeWidth={1.6} />
              <div className="flex-1 min-w-0">
                <p className="font-title text-lg truncate">{d.supplier_name || 'Fournisseur inconnu'}</p>
                <p className="text-[12px] text-n600">{d.type} · N° {d.number || '—'} · {formatDate(d.date)} · {formatEuro(d.total_ht)}</p>
              </div>
              <Tag tone={DOC_STATUS[d.status]?.tone}>{DOC_STATUS[d.status]?.label || d.status}</Tag>
            </button>
          ))}
        </div>
      )}
      {docId && <DocumentModal documentId={docId} onClose={() => setDocId(null)} />}
    </div>
  );
}

/* ---------------- Commandes ---------------- */
function Commandes() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ['achats-orders'], queryFn: () => achatsApi.orders() });
  const [editing, setEditing] = useState(null);
  const refresh = () => { qc.invalidateQueries({ queryKey: ['achats-orders'] }); qc.invalidateQueries({ queryKey: ['achats-summary'] }); qc.invalidateQueries({ queryKey: ['achats-a-traiter'] }); };
  if (isLoading) return <Spinner />;
  return (
    <div className="space-y-3" data-testid="achats-commandes">
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => setEditing({ supplier_name: '', status: 'brouillon', lines: [] })} data-testid="add-order-btn"><Plus className="w-4 h-4" strokeWidth={1.6} /> Nouvelle commande</Button>
      </div>
      {!data.length ? <EmptyState title="Aucune commande" description="Les commandes créées depuis les alertes ou « À commander » apparaîtront ici." testId="commandes-empty" /> : data.map((o) => (
        <button key={o.id} onClick={() => setEditing(o)} className="w-full vd-card p-3 text-left hover:bg-surface transition-colors" data-testid={`order-${o.id}`}>
          <div className="flex items-center gap-3 mb-2">
            <Truck className="w-5 h-5 text-n600 shrink-0" strokeWidth={1.6} />
            <div className="flex-1 min-w-0">
              <p className="font-title text-lg truncate">{o.supplier_name}</p>
              <p className="text-[12px] text-n600">{formatDate(o.date)} · {o.total_bottles} btl · {formatEuro(o.total_ht)}</p>
            </div>
            <Tag tone={ORDER_STATUS[o.status]?.tone}>{ORDER_STATUS[o.status]?.label || o.status}</Tag>
          </div>
          {o.lines?.length > 0 && (
            <ul className="text-[13px] text-n700 pl-8 space-y-0.5">
              {o.lines.map((l, i) => (
                <li key={i} className="flex justify-between gap-2">
                  <span className="truncate">{l.quantite} × {l.libelle}</span>
                  <span className="nums text-n500 shrink-0">{formatEuro(l.pu_ht)}</span>
                </li>
              ))}
            </ul>
          )}
        </button>
      ))}
      {editing && <OrderModal order={editing} onClose={() => setEditing(null)} onSaved={refresh} />}
    </div>
  );
}

function OrderModal({ order, onClose, onSaved }) {
  const [form, setForm] = useState({
    id: order.id, supplier_name: order.supplier_name || '', status: order.status || 'brouillon',
    date: (order.date || '').slice(0, 10), lines: (order.lines || []).map((l) => ({ ...l })),
  });
  const [busy, setBusy] = useState(false);
  const setLine = (i, patch) => setForm((f) => ({ ...f, lines: f.lines.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { libelle: '', quantite: 6, pu_ht: null, format: '750' }] }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy(true);
    try { await achatsApi.saveOrder({ ...form, lines: form.lines }); toast.success('Commande enregistrée'); onSaved(); onClose(); }
    catch (e) { toast.error(e.message || 'Échec'); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!order.id || !window.confirm('Supprimer cette commande ?')) return;
    await achatsApi.deleteOrder(order.id); toast.success('Commande supprimée'); onSaved(); onClose();
  };

  return (
    <Overlay onClose={onClose} wide>
      <h3 className="font-title text-2xl mb-4" data-testid="order-modal">{order.id ? 'Modifier' : 'Nouvelle'} commande</h3>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <label className="block col-span-1"><span className="text-[12px] text-n600">Fournisseur</span>
          <input className="vd-input" value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} data-testid="order-supplier" /></label>
        <label className="block"><span className="text-[12px] text-n600">Date</span>
          <input type="date" className="vd-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="order-date" /></label>
        <label className="block"><span className="text-[12px] text-n600">Statut</span>
          <select className="vd-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="order-status">
            {Object.entries(ORDER_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select></label>
      </div>
      <p className="kicker mb-1">Lignes</p>
      <div className="space-y-2 mb-3">
        {form.lines.map((l, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`order-line-${i}`}>
            <input className="vd-input flex-1" placeholder="Libellé" value={l.libelle || ''} onChange={(e) => setLine(i, { libelle: e.target.value })} />
            <input type="number" min="0" className="vd-input w-16 nums" value={l.quantite ?? ''} onChange={(e) => setLine(i, { quantite: Number(e.target.value) })} />
            <input type="number" step="0.01" className="vd-input w-24 nums" placeholder="PU HT" value={l.pu_ht ?? ''} onChange={(e) => setLine(i, { pu_ht: e.target.value === '' ? null : Number(e.target.value) })} />
            <button onClick={() => rmLine(i)} className="p-1.5 text-n500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        <button onClick={addLine} className="text-[13px] text-gold700 hover:underline flex items-center gap-1" data-testid="order-add-line"><Plus className="w-4 h-4" /> Ajouter une ligne</button>
      </div>
      <div className="flex justify-between gap-2 mt-5">
        {order.id ? <Button onClick={remove} className="text-red-700" data-testid="order-delete"><Trash2 className="w-4 h-4" /> Supprimer</Button> : <span />}
        <div className="flex gap-2">
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="primary" disabled={busy || !form.supplier_name.trim()} onClick={save} data-testid="order-save">Enregistrer</Button>
        </div>
      </div>
    </Overlay>
  );
}

/* ---------------- Fournisseurs ---------------- */
function Fournisseurs() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ['achats-suppliers'], queryFn: achatsApi.suppliers });
  const [editing, setEditing] = useState(null);

  const save = async (payload) => {
    await achatsApi.saveSupplier(payload);
    qc.invalidateQueries({ queryKey: ['achats-suppliers'] });
    qc.invalidateQueries({ queryKey: ['achats-summary'] });
    setEditing(null);
    toast.success('Fournisseur enregistré');
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer ce fournisseur ?')) return;
    await achatsApi.deleteSupplier(id);
    qc.invalidateQueries({ queryKey: ['achats-suppliers'] });
    setEditing(null);
    toast.success('Fournisseur supprimé');
  };

  if (isLoading) return <Spinner />;
  return (
    <div data-testid="achats-fournisseurs">
      <div className="flex justify-end mb-3">
        <Button variant="primary" onClick={() => setEditing({ name: '' })} data-testid="add-supplier-btn">
          <Plus className="w-4 h-4" strokeWidth={1.6} /> Ajouter un fournisseur
        </Button>
      </div>
      {!data.length ? (
        <EmptyState title="Aucun fournisseur" description="Ajoutez vos domaines et cavistes." testId="fournisseurs-empty" />
      ) : (
        <div className="grid sm:grid-cols-2 gap-2">
          {data.map((s) => (
            <button key={s.id} onClick={() => setEditing(s)} className="vd-card p-3 text-left hover:bg-surface transition-colors" data-testid={`supplier-${s.id}`}>
              <p className="font-title text-lg">{s.name}</p>
              <p className="text-[12px] text-n600">{s.region || '—'} · {s.order_count} commande(s) · {s.document_count} document(s)</p>
              {s.email && <p className="text-[12px] text-n500">{s.email}</p>}
            </button>
          ))}
        </div>
      )}
      {editing && <SupplierModal supplier={editing} onSave={save} onDelete={remove} onClose={() => setEditing(null)} />}
    </div>
  );
}

function SupplierModal({ supplier, onSave, onDelete, onClose }) {
  const [form, setForm] = useState({ name: supplier.name || '', email: supplier.email || '', phone: supplier.phone || '', region: supplier.region || '', id: supplier.id });
  return (
    <Overlay onClose={onClose}>
      <h3 className="font-title text-2xl mb-4">{supplier.id ? 'Modifier' : 'Ajouter'} un fournisseur</h3>
      <div className="space-y-3">
        {[['name', 'Nom'], ['region', 'Région'], ['email', 'Email'], ['phone', 'Téléphone']].map(([k, label]) => (
          <label key={k} className="block">
            <span className="text-[12px] text-n600">{label}</span>
            <input className="vd-input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`supplier-field-${k}`} />
          </label>
        ))}
      </div>
      <div className="flex justify-between gap-2 mt-5">
        {supplier.id ? <Button onClick={() => onDelete(supplier.id)} className="text-red-700" data-testid="supplier-delete-btn"><Trash2 className="w-4 h-4" /> Supprimer</Button> : <span />}
        <div className="flex gap-2">
          <Button onClick={onClose}>Annuler</Button>
          <Button variant="primary" disabled={!form.name.trim()} onClick={() => onSave(form)} data-testid="supplier-save-btn">Enregistrer</Button>
        </div>
      </div>
    </Overlay>
  );
}

/* ---------------- Document detail + rapprochement ---------------- */
function DocumentModal({ documentId, onClose }) {
  const qc = useQueryClient();
  const { data: doc, isLoading } = useQuery({ queryKey: ['achats-document', documentId], queryFn: () => achatsApi.document(documentId) });
  const { data: orders = [] } = useQuery({ queryKey: ['achats-orders'], queryFn: () => achatsApi.orders() });
  const [orderId, setOrderId] = useState('');
  const [recon, setRecon] = useState(null);
  const [busy, setBusy] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const openOrders = orders.filter((o) => o.status !== 'close');

  const startEdit = () => setEditForm({
    supplier_name: doc.supplier_name || '', number: doc.number || '', date: (doc.date || '').slice(0, 10),
    type: doc.type || 'facture', status: doc.status || 'a_valider',
    lines: (doc.lines || []).map((l) => ({ ...l })),
  });
  const setEditLine = (i, patch) => setEditForm((f) => ({ ...f, lines: f.lines.map((l, j) => j === i ? { ...l, ...patch } : l) }));
  const saveEdit = async () => {
    setBusy(true);
    try {
      await achatsApi.updateDocument(documentId, editForm);
      qc.invalidateQueries({ queryKey: ['achats-document', documentId] });
      qc.invalidateQueries({ queryKey: ['achats-documents'] });
      qc.invalidateQueries({ queryKey: ['achats-summary'] });
      toast.success('Document mis à jour'); setEditForm(null);
    } catch (e) { toast.error(e.message || 'Échec'); } finally { setBusy(false); }
  };
  const removeDoc = async () => {
    if (!window.confirm('Supprimer ce document ?')) return;
    await achatsApi.deleteDocument(documentId);
    qc.invalidateQueries({ queryKey: ['achats-documents'] });
    qc.invalidateQueries({ queryKey: ['achats-summary'] });
    toast.success('Document supprimé'); onClose();
  };

  const doReconcile = async (oid) => {
    setBusy(true);
    try {
      const r = await achatsApi.reconcile(documentId, oid || null);
      setRecon(r);
    } finally { setBusy(false); }
  };

  const validate = async () => {
    setBusy(true);
    try {
      const lines = (doc.lines || []).map((l) => ({
        new_cuvee: { name: l.libelle_brut || l.libelle }, new_domain: { name: doc.supplier_name || 'Fournisseur' },
        libelle_brut: l.libelle_brut, quantity: l.quantite, pu_ht: l.pu_ht, format: l.format || '750', year: l.year || 'NV',
      }));
      const res = await achatsApi.validateDocument(documentId, lines, true);
      qc.invalidateQueries({ queryKey: ['achats-documents'] });
      qc.invalidateQueries({ queryKey: ['achats-a-traiter'] });
      qc.invalidateQueries({ queryKey: ['achats-summary'] });
      qc.invalidateQueries({ queryKey: ['stock'] });
      toast.success(`${res.total_bottles} bouteille(s) entrées en stock`);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Échec de la validation');
    } finally { setBusy(false); }
  };

  const KIND = {
    ok: { label: 'Conforme', cls: 'text-green-700' },
    prix: { label: 'Écart de prix', cls: 'text-amber-700' },
    quantite: { label: 'Écart de quantité', cls: 'text-amber-700' },
    manquant: { label: 'Non livré', cls: 'text-red-700' },
    en_trop: { label: 'Non commandé', cls: 'text-red-700' },
  };

  return (
    <Overlay onClose={onClose} wide>
      {isLoading || !doc ? <Spinner /> : (
        <div data-testid="document-modal">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h3 className="font-title text-2xl">{doc.supplier_name || 'Document'} · {doc.type}</h3>
              <p className="text-[13px] text-n600">N° {doc.number || '—'} · {formatDate(doc.date)} · {formatEuro(doc.total_ht)}</p>
            </div>
            <div className="flex items-center gap-2">
              <Tag tone={DOC_STATUS[doc.status]?.tone}>{DOC_STATUS[doc.status]?.label || doc.status}</Tag>
              {!editForm && <Button className="h-8" onClick={startEdit} data-testid="document-edit-btn">Modifier</Button>}
              <button onClick={removeDoc} className="p-1.5 text-n500 hover:text-red-700" title="Supprimer" data-testid="document-delete-btn"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>

          {editForm ? (
            <div className="mb-4" data-testid="document-edit-form">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <label className="block"><span className="text-[12px] text-n600">Fournisseur</span>
                  <input className="vd-input" value={editForm.supplier_name} onChange={(e) => setEditForm({ ...editForm, supplier_name: e.target.value })} data-testid="document-supplier" /></label>
                <label className="block"><span className="text-[12px] text-n600">Numéro</span>
                  <input className="vd-input" value={editForm.number} onChange={(e) => setEditForm({ ...editForm, number: e.target.value })} /></label>
                <label className="block"><span className="text-[12px] text-n600">Date</span>
                  <input type="date" className="vd-input" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} /></label>
                <label className="block"><span className="text-[12px] text-n600">Type</span>
                  <select className="vd-input" value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}>
                    <option value="facture">Facture</option><option value="bon_livraison">Bon de livraison</option>
                  </select></label>
                <label className="block"><span className="text-[12px] text-n600">Statut</span>
                  <select className="vd-input" value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                    {Object.entries(DOC_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select></label>
              </div>
              <p className="kicker mb-1">Lignes</p>
              <div className="space-y-2">
                {editForm.lines.map((l, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`document-edit-line-${i}`}>
                    <input className="vd-input flex-1" value={l.libelle_brut || l.libelle || ''} onChange={(e) => setEditLine(i, { libelle_brut: e.target.value, libelle: e.target.value })} />
                    <input type="number" className="vd-input w-16 nums" value={l.quantite ?? ''} onChange={(e) => setEditLine(i, { quantite: Number(e.target.value) })} />
                    <input type="number" step="0.01" className="vd-input w-24 nums" value={l.pu_ht ?? ''} onChange={(e) => setEditLine(i, { pu_ht: e.target.value === '' ? null : Number(e.target.value) })} />
                    <button onClick={() => setEditForm((f) => ({ ...f, lines: f.lines.filter((_, j) => j !== i) }))} className="p-1.5 text-n500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => setEditForm((f) => ({ ...f, lines: [...f.lines, { libelle: '', quantite: 1, pu_ht: null }] }))} className="text-[13px] text-gold700 hover:underline flex items-center gap-1"><Plus className="w-4 h-4" /> Ajouter une ligne</button>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button onClick={() => setEditForm(null)}>Annuler</Button>
                <Button variant="primary" onClick={saveEdit} disabled={busy} data-testid="document-save-btn">Enregistrer</Button>
              </div>
            </div>
          ) : (
          <>
          {/* Lignes du document */}
          <div className="vd-card divide-y divide-hair mb-4">
            {(doc.lines || []).map((l, i) => (
              <div key={i} className="p-2.5 flex items-center justify-between gap-2 text-[13px]" data-testid={`doc-line-${i}`}>
                <span className="truncate">{l.quantite} × {l.libelle_brut || l.libelle}</span>
                <span className="nums text-n600 shrink-0">{formatEuro(l.pu_ht)} · {formatEuro(l.total_ht)}</span>
              </div>
            ))}
            {!(doc.lines || []).length && <p className="p-3 text-[13px] text-n500">Aucune ligne extraite.</p>}
          </div>

          {/* Rapprochement avec une commande */}
          {doc.status === 'a_valider' && (
            <div className="mb-4">
              <p className="text-[12px] text-n600 mb-1">Rapprocher avec une commande</p>
              <div className="flex flex-wrap gap-2 items-center">
                <select className="vd-input max-w-xs" value={orderId} onChange={(e) => setOrderId(e.target.value)} data-testid="reconcile-order-select">
                  <option value="">— Aucune —</option>
                  {openOrders.map((o) => <option key={o.id} value={o.id}>{o.supplier_name} · {o.total_bottles} btl · {formatDate(o.date)}</option>)}
                </select>
                <Button onClick={() => doReconcile(orderId)} disabled={busy || !orderId} data-testid="reconcile-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Rapprocher
                </Button>
              </div>
            </div>
          )}

          {/* Résultat du rapprochement */}
          {recon && (
            <div className="vd-card p-3 mb-4" data-testid="reconcile-result">
              <p className={`flex items-center gap-2 text-[13.5px] mb-2 ${recon.conforme ? 'text-green-700' : 'text-amber-700'}`}>
                {recon.conforme ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                {recon.conforme ? 'Conforme à la commande' : `${recon.ecart_count} écart(s) détecté(s)`}
              </p>
              <div className="space-y-1">
                {recon.discrepancies.map((d, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-[13px]" data-testid={`discrepancy-${i}`}>
                    <span className="truncate">{d.libelle}</span>
                    <span className={`shrink-0 ${KIND[d.kind]?.cls}`}>
                      {KIND[d.kind]?.label}
                      {d.kind === 'quantite' && ` (cmd ${d.order_qty} → reçu ${d.doc_qty})`}
                      {d.kind === 'prix' && ` (cmd ${formatEuro(d.order_pu)} → ${formatEuro(d.doc_pu)})`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {doc.status === 'a_valider' && (
            <div className="flex justify-end gap-2">
              <Button onClick={onClose}>Fermer</Button>
              <Button variant="primary" onClick={validate} disabled={busy || !(doc.lines || []).length} data-testid="validate-document-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Valider et entrer en stock
              </Button>
            </div>
          )}
          </>
          )}
        </div>
      )}
    </Overlay>
  );
}

function Overlay({ children, onClose, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 animate-fade-in" onClick={onClose}>
      <div className={`bg-paper border border-hair rounded-lg shadow-xl p-5 w-full ${wide ? 'max-w-2xl' : 'max-w-md'} max-h-[85vh] overflow-auto`} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}
